import { useState, type ChangeEvent, type ReactNode } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import BaseInputTemplate from '@rjsf/core/lib/components/templates/BaseInputTemplate'
import {
  type ArrayFieldTemplateProps,
  type BaseInputTemplateProps,
  type DescriptionFieldProps,
  type FieldTemplateProps,
  type ObjectFieldTemplateProps,
  type RJSFSchema,
  type TitleFieldProps,
  type WidgetProps,
} from '@rjsf/utils'
import { Button } from '../../../components/Button/Button'
import { formatCurrencyAmount } from '../../../common/intlFormatters'
import styles from '../AccountOpeningWizard.module.scss'
import { WARNING_BORDER } from '../constants'
import { type WizardFormContext } from '../domain/types'

const missingMark = (id: string, formContext: unknown): string | null =>
  (formContext as WizardFormContext | undefined)?.missingFieldIds?.has(id) ? WARNING_BORDER : null

const missingBox = (id: string, formContext: unknown): string[] | null => {
  const mark = missingMark(id, formContext)
  return mark ? [mark, 'zocks-p-0.75 rounded-2'] : null
}

const isLocked = (props: { disabled?: boolean; readonly?: boolean }): boolean => !!props.disabled || !!props.readonly

const _NOT_SET = 'Not set'

const _WIDGET_HINT = 'x-zocks-widget'

const widgetHint = (schema: RJSFSchema): string | undefined => {
  const hint = (schema as Record<string, unknown>)[_WIDGET_HINT]
  return typeof hint === 'string' ? hint : undefined
}

const isArrayEntry = (id: string): boolean => /_\d+$/.test(id)

const isGroup = (schema: RJSFSchema | undefined): boolean => schema?.type === 'object' || schema?.type === 'array'

const isFullRow = (schema: RJSFSchema | undefined): boolean =>
  !schema || isGroup(schema) || schema.type === 'boolean' || !!schema.enum || !!schema.oneOf || !!schema.anyOf

const singularize = (label: string): string => {
  if (/\bof\b/i.test(label)) {
    return label
  }
  if (/ies$/i.test(label)) {
    return label.replace(/ies$/i, 'y')
  }
  if (/(ss|sh|ch|x|z)es$/i.test(label)) {
    return label.replace(/es$/i, '')
  }
  return /[^s]s$/i.test(label) ? label.replace(/s$/i, '') : label
}

const addLabel = (heading: string | undefined): string => {
  if (!heading) {
    return 'Add'
  }
  const noun = singularize(heading)
    .split(' ')
    .map((word) => (word === word.toUpperCase() ? word : word.charAt(0).toLowerCase() + word.slice(1)))
    .join(' ')
  return `Add ${noun}`
}

export const CheckboxWidget = (props: WidgetProps) => (
  <Form.Check
    type='checkbox'
    id={props.id}
    label={props.label || props.schema.title}
    checked={props.value ?? false}
    disabled={isLocked(props)}
    onChange={(event: ChangeEvent<HTMLInputElement>) => props.onChange(event.target.checked)}
  />
)

export const RadioWidget = (props: WidgetProps) => {
  const enumOptions = props.options.enumOptions ?? []
  const disabled = isLocked(props)

  const option = (key: string, id: string, label: string, checked: boolean, value: unknown) => (
    <Form.Check key={key} type='radio' id={id} name={props.id} label={label} checked={checked} disabled={disabled} onChange={() => props.onChange(value)} />
  )

  return (
    <div id={props.id} className={classNames('d-flex flex-column zocks-gap-0.5', missingBox(props.id, props.registry.formContext))}>
      {props.required ? null : option('__unset', `${props.id}__unset`, _NOT_SET, props.value === undefined, undefined)}
      {enumOptions.map((entry, index) => option(`${entry.label}-${index}`, `${props.id}_${index}`, entry.label, entry.value === props.value, entry.value))}
    </div>
  )
}

export const SelectWidget = (props: WidgetProps) => {
  const enumOptions = props.options.enumOptions ?? []

  if (!props.multiple && enumOptions.length > 0 && widgetHint(props.schema) === 'radio') {
    return <RadioWidget {...props} />
  }

  const selectedIndex = enumOptions.findIndex((option) => option.value === props.value)

  return (
    <Form.Select
      id={props.id}
      value={selectedIndex === -1 ? '' : String(selectedIndex)}
      disabled={isLocked(props)}
      required={props.required}
      className={classNames(missingMark(props.id, props.registry.formContext))}
      onChange={(event) => props.onChange(event.target.value === '' ? undefined : enumOptions[Number(event.target.value)].value)}
    >
      <option value=''>{_NOT_SET}</option>
      {enumOptions.map((option, index) => (
        <option key={`${option.label}-${index}`} value={String(index)}>
          {option.label}
        </option>
      ))}
    </Form.Select>
  )
}

const currencyAmount = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

const asText = (value: unknown): string => (value === undefined || value === null ? '' : String(value))

export const CurrencyWidget = (props: WidgetProps) => {
  const [draft, setDraft] = useState<string | null>(null)

  const amount = currencyAmount(props.value)
  const plain = amount !== undefined ? String(amount) : asText(props.value)

  const change = (text: string) => {
    setDraft(text)
    const cleaned = text.replace(/[^\d.-]/g, '')
    if (cleaned === '') {
      props.onChange(undefined)
      return
    }
    const parsed = Number(cleaned)
    if (Number.isFinite(parsed)) {
      props.onChange(parsed)
    }
  }

  return (
    <Form.Control
      type='text'
      inputMode='decimal'
      id={props.id}
      value={draft ?? (amount !== undefined ? formatCurrencyAmount(amount) : plain)}
      disabled={isLocked(props)}
      required={props.required}
      placeholder={props.placeholder || _NOT_SET}
      className={classNames(missingMark(props.id, props.registry.formContext))}
      onFocus={() => setDraft(plain)}
      onChange={(event: ChangeEvent<HTMLInputElement>) => change(event.target.value)}
      onBlur={() => setDraft(null)}
    />
  )
}

const isCurrencyField = (schema: RJSFSchema): boolean => widgetHint(schema) === 'currency' && (schema.type === 'number' || schema.type === 'integer')

export const WizardBaseInputTemplate = (props: BaseInputTemplateProps) =>
  isCurrencyField(props.schema) ? (
    <CurrencyWidget {...props} />
  ) : (
    <BaseInputTemplate
      {...props}
      className={classNames('form-control', missingMark(props.id, props.registry.formContext))}
      placeholder={props.placeholder || _NOT_SET}
    />
  )

export const WizardTitleFieldTemplate = ({ id, title, required }: TitleFieldProps) =>
  title ? (
    <div id={id} className='paragraph-medium medium'>
      {title}
      {required ? ' *' : ''}
    </div>
  ) : null

export const WizardDescriptionFieldTemplate = ({ id, description }: DescriptionFieldProps) =>
  description ? (
    <div id={id} className='paragraph-small zocks-text-gray-600'>
      {description}
    </div>
  ) : null

export const WizardFieldTemplate = (props: FieldTemplateProps) => {
  const { id, label, required, description, errors, children, hidden, displayLabel } = props
  if (hidden) {
    return null
  }
  const labelled = displayLabel && !!label && !isArrayEntry(id)
  return (
    <div className='d-flex flex-column zocks-gap-0.25 zocks-mb-0.75'>
      {labelled ? (
        <Form.Label htmlFor={id} className='paragraph-small medium mb-0'>
          {label}
          {required ? ' *' : ''}
        </Form.Label>
      ) : null}
      {displayLabel ? description : null}
      {children}
      {errors}
    </div>
  )
}

const groupBody = (properties: ObjectFieldTemplateProps['properties'], schema: RJSFSchema): ReactNode => {
  const rows: { full: boolean; fields: ObjectFieldTemplateProps['properties'] }[] = []
  properties.forEach((property) => {
    const full = isFullRow(schema.properties?.[property.name] as RJSFSchema | undefined)
    const openRow = rows[rows.length - 1]
    if (full || !openRow || openRow.full) {
      rows.push({ full, fields: [property] })
      return
    }
    openRow.fields.push(property)
  })

  return rows.map((row) =>
    row.full ? (
      <div key={row.fields[0].name}>{row.fields[0].content}</div>
    ) : (
      <div key={row.fields[0].name} className={styles.fieldRow}>
        {row.fields.map((property) => (
          <div key={property.name}>{property.content}</div>
        ))}
      </div>
    ),
  )
}

export const WizardObjectFieldTemplate = (props: ObjectFieldTemplateProps) => {
  const { title, description, properties, idSchema, required, registry, schema } = props
  const isRoot = idSchema.$id === 'root'

  if (isRoot) {
    return (
      <div className='d-flex flex-column'>
        {description ? <div className='paragraph-small zocks-text-gray-600 zocks-mb-0.75'>{description}</div> : null}
        {properties.map((property) => (
          <div
            key={property.name}
            className={classNames({
              'zocks-pt-0.75 border-top zocks-border-color-gray-300': isGroup(schema.properties?.[property.name] as RJSFSchema | undefined),
            })}
          >
            {property.content}
          </div>
        ))}
      </div>
    )
  }

  const isEntry = isArrayEntry(idSchema.$id)

  return (
    <fieldset
      id={idSchema.$id}
      className={classNames('d-flex flex-column zocks-gap-0.75', missingBox(idSchema.$id, registry.formContext), {
        'zocks-mb-0.75': !isEntry,
      })}
    >
      {title && !isEntry ? (
        <legend className='paragraph-medium medium zocks-mb-0.75 float-none w-auto'>
          {title}
          {required ? ' *' : ''}
        </legend>
      ) : null}
      {description ? <div className='paragraph-small zocks-text-gray-600'>{description}</div> : null}
      {groupBody(properties, schema)}
    </fieldset>
  )
}

export const WizardArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
  const { title, items, canAdd, onAddClick, schema, idSchema, registry } = props
  const heading = typeof schema.title === 'string' ? schema.title : title
  const entryOpensWithLabel = (schema.items as RJSFSchema | undefined)?.type === 'object'

  return (
    <div id={idSchema.$id} className={classNames('d-flex flex-column zocks-gap-0.75 zocks-mb-0.75', missingBox(idSchema.$id, registry.formContext))}>
      {heading ? <div className='paragraph-medium medium'>{heading}</div> : null}

      {items.length === 0 ? <div className='paragraph-small zocks-text-gray-600'>None added.</div> : null}

      {items.map((item) => (
        <div key={item.key} className='d-flex align-items-start zocks-gap-0.5'>
          <div className='flex-grow-1'>{item.children}</div>
          {item.hasRemove ? (
            <Button
              variant='secondary'
              aria-label='Remove entry'
              className={classNames('flex-shrink-0', { [styles.entryAction]: entryOpensWithLabel })}
              disabled={isLocked(item)}
              onClick={item.onDropIndexClick(item.index)}
            >
              <i className='bi bi-trash' />
            </Button>
          ) : null}
        </div>
      ))}

      {canAdd && !isLocked(props) ? (
        <Button variant='secondary' className='align-self-start d-flex align-items-center zocks-gap-0.5' onClick={onAddClick}>
          <i className='bi bi-plus-circle' />
          {addLabel(heading)}
        </Button>
      ) : null}
    </div>
  )
}
